import { useRouter } from 'next/router';
import { FC } from 'react';
import { useSwrStale } from './hooks';
import { useBlog } from './hooks.context';

const defaultDataResolver = <T,>(
  dataKey: string | number | symbol,
  data: Record<string, Iterable<unknown>>,
  initialData: Record<string, Iterable<unknown>>,
) => ({ [dataKey]: { ...initialData, ...(data || {}) } });

type SiteExtensionConfig<T> = {
  incrementData?: boolean;
  param?: string; // query param to look for
  dataKey: keyof T; // property populate
  dataResolver?: typeof defaultDataResolver;
};

const defaultParam = 'sites';

interface ErrorResponse {
  error: string;
}

interface Props {
  incrementData?: boolean; // boolean really
}

interface ObjectData {
  [dataKey: string]: Record<string, Iterable<unknown>>;
}
interface ArrayData {
  [dataKey: string]: unknown[];
}

const resolveSitesString = (str: string, subsite: string) =>
  str
    .split(',')
    .filter((s) => s !== subsite)
    .join(',');

export const withSiteExtensionData = <T extends ObjectData | ArrayData>(
  Component: FC<any>,
  {
    param = defaultParam,
    dataKey,
    incrementData: incrementDataSetting = true,
    dataResolver = defaultDataResolver,
  }: SiteExtensionConfig<T>,
): FC<T & Props> => {
  const DataProvider = ({
    [dataKey]: initialValue,
    incrementData: incrementDataProp,
    ...props
  }: T) => {
    const incrementData =
      typeof incrementDataProp === 'boolean'
        ? incrementDataProp
        : incrementDataSetting;
    const subsite = useBlog(false);
    const {
      query: { [param]: value },
    } = useRouter();
    const sitesQueryString =
      value?.length &&
      resolveSitesString(
        Array.isArray(value) ? value.join(',') : value,
        subsite,
      );
    const { data } = useSwrStale<typeof initialValue | ErrorResponse>(
      sitesQueryString
        ? `/entities/${String(dataKey)}?sites=${sitesQueryString}`
        : null,
    );

    if (!data || typeof (data as ErrorResponse)?.error !== 'undefined') {
      return (
        <Component
          {...props}
          {...{ [dataKey]: initialValue }}
          isLoading={sitesQueryString && !data}
          error={(data as ErrorResponse)?.error}
        />
      );
    }

    if (typeof initialValue === 'object' && initialValue && incrementData) {
      const isArrayData = Array.isArray(initialValue) && Array.isArray(data);

      const incrementedData = isArrayData
        ? {
            [dataKey]: [
              ...(initialValue as Extract<T, ArrayData>[keyof T]),
              ...((data || []) as unknown[] as Extract<T, ArrayData>[keyof T]),
            ],
          }
        : dataResolver<Extract<T, ObjectData>[keyof T]>(
            dataKey,
            data as Extract<T, ObjectData>[keyof T],
            initialValue as Extract<T, ObjectData>[keyof T],
          );

      // Filter out content
      if (dataKey === 'jobs') {
        return <Component {...props} {...{ [dataKey]: data }} />;
      }

      return <Component {...props} {...incrementedData} />;
    }

    return <Component {...props} {...{ [dataKey]: data }} />;
  };

  return DataProvider;
};

export default withSiteExtensionData;
